import { AfterViewInit, Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, lastValueFrom } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { ProbateSummary } from '../Models/ProbateSummary';
import { User } from '../Models/User';
import { Note } from '../Models/Note';
import { Moment } from 'moment';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-probate-list',
  templateUrl: './probate-list.component.html',
  styleUrls: ['./probate-list.component.css'],
  standalone: false
})
export class ProbateListComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) loggedInUser?: User = undefined;

  private http = inject(HttpClient);
  route = inject(ActivatedRoute);
  router = inject(Router);

  displayedColumns: string[] = [
    'select',
    'caseReferenceNumber',
    'fullNameOfDeceased',
    'clientCharitableBequestCount',
    'nonClientCharitableBequestCount',
    'charitableBequestCount',
    'nonCharitableBequestCount',
    'dateOfIssue',
    'weekOld',
    'assignedUser',
    'hasNotes',
    'status'
  ];

  dataSource = new MatTableDataSource<ProbateSummary>([]);
  selection = new SelectionModel<ProbateSummary>(true, []);
  // Mirrors what MatTable actually paints (after filter → sort → paginate) so the
  // master checkbox and "is all selected?" checks always operate on the visible rows.
  private renderedRows: ProbateSummary[] = [];
  users: User[] = [];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  selectedUserFilter: User | 'unassigned' | null = null;
  assignToUserControl = new FormControl<User | 'unassign' | null>(null);

  // Status filter options matching EstateWorkflowStatus enum (numeric values)
  statusOptions = [
    { value: 0, label: 'Unreadable' },
    { value: 1, label: 'Processing' },
    { value: 2, label: 'Reported' },
    { value: 3, label: 'Verified' },
    { value: 4, label: 'Completed' }
  ];
  // Default: Processing (1), Reported (2)
  selectedStatusFilters: number[] = [1, 2];

  ProbateSummaries: ProbateSummary[] = [];

  // Cache of notes per probate, populated lazily on hover over the "Y" in the Has Notes column.
  // The loading state is tracked separately so the cache itself is always Note[] (keeps the template strict-mode happy).
  notesByProbateId: { [probateId: number]: Note[] } = {};
  notesLoadingProbateIds = new Set<number>();

  private loggedInUserTag = "";
  private loggedInUser2?: User = undefined;
  private hasInitialisedUserFilter = false;

  // ✅ NEW: Page size persistence
  pageSize = 50;

  // Show Non-Charitable filter (default unchecked = hide non-charitable)
  showNonCharitable = false;

  // Date filter
  startDate: Moment | null = null;
  endDate: Moment | null = null;

  constructor(private snackBar: MatSnackBar, private userService: UserService) { }

  ngOnInit(): void {
    // Load saved page size
    const saved = localStorage.getItem('probate-page-size');
    if (saved) {
      this.pageSize = +saved;
    }

    this.extractRouteValues();
    this.populateComponents();

    this.dataSource.filterPredicate = (data: ProbateSummary, filter: string) => {
      const searchStr = (
        data.probateId + ' ' +
        data.deceasedName + ' ' +
        data.subscribedCharitableBequestCount + ' ' +
        data.nonSubscribedCharitableBequestCount + ' ' +
        data.nonCharitableBequestCount// + ' ' +
        // (data.assignedUser ? data.assignedUser.firstName + ' ' + data.assignedUser.lastName : '')
      ).toLowerCase();
      return searchStr.includes(filter);
    };
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item: ProbateSummary, property: string) => {
      switch (property) {
        case 'fullNameOfDeceased': return item.deceasedName?.toLowerCase() ?? '';
        case 'assignedUser': return item.assignedUser ? `${item.assignedUser.firstName} ${item.assignedUser.lastName}`.toLowerCase() : '';
        // Week Old: older issue date = higher week count. Sort on the date itself (ascending date = lower week count).
        case 'weekOld': return item.dateOfIssue ? new Date(item.dateOfIssue).getTime() : Number.MAX_SAFE_INTEGER;
        case 'reportedBy': return item.reportedByUser ? `${item.reportedByUser.firstName} ${item.reportedByUser.lastName}`.toLowerCase() : '';
        case 'verifiedBy': return item.verifiedByUser ? `${item.verifiedByUser.firstName} ${item.verifiedByUser.lastName}`.toLowerCase() : '';
        // Has Notes column displays Y/N based on noteCount > 0. Sort with 1/0 so the visual
        // grouping (all N rows together, all Y rows together) matches the displayed value.
        case 'hasNotes': return item.noteCount > 0 ? 1 : 0;
        default: return (item as any)[property];
      }
    };

    // Apply paginator and restore saved page size
    this.paginator.pageSize = this.pageSize;
    this.dataSource.paginator = this.paginator;

    // Whenever the rendered rows change (initial load / filter / search), pre-fetch notes
    // for any row with noteCount > 0 that we don't already have cached. The hover popover
    // then renders from cache with no HTTP call and no (mouseenter) Angular event handler.
    this.dataSource.connect().subscribe(rows => {
      this.renderedRows = rows;
      this.preloadNotesForVisibleProbates();
    });

    // A new sort order is a new selection intent — drop the prior selection so the
    // user starts fresh on the re-sorted view. Without this, a follow-up "Select all"
    // would silently combine the pre-sort selection with the new visible rows.
    this.sort.sortChange.subscribe(() => this.selection.clear());
  }

  // ✅ NEW: Page change handler
  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
    localStorage.setItem('probate-page-size', String(event.pageSize));
  }

  // Pre-fetches notes for every visible probate that has noteCount > 0 so the hover popover
  // can show them instantly with no per-hover HTTP call and no Angular event handler
  // (no event handler → no change-detection cycle on hover → no jank).
  // Idempotent: only fetches rows that aren't already cached or in flight.
  private preloadNotesForVisibleProbates(): void {
    const rows = this.dataSource?.data ?? [];
    for (const row of rows) {
      if (row.noteCount <= 0) continue;
      const id = row.probateId;
      if (this.notesByProbateId[id] !== undefined || this.notesLoadingProbateIds.has(id)) continue;
      this.notesLoadingProbateIds.add(id);
      const params = new HttpParams().set('ProbateId', id).set('User', this.loggedInUserTag);
      this.http.get<Note[]>('/charitycentral/GetNotes', { params }).subscribe({
        next: notes => {
          this.notesByProbateId[id] = notes ?? [];
          this.notesLoadingProbateIds.delete(id);
        },
        error: () => {
          this.notesLoadingProbateIds.delete(id);
        }
      });
    }
  }

  nameSearchValue: string = '';
  isNameSearchActive: boolean = false;
  isDateSearchActive: boolean = false;

  applyNameSearch() {
    const name = this.nameSearchValue.trim();
    if (!name) return;
    const params = new HttpParams().set('name', name).set('user', this.loggedInUserTag);
    this.http.get<ProbateSummary[]>('/charitycentral/getprobatesummariesbyname', { params })
      .subscribe({
        next: (results) => {
          this.selection.clear();
          this.dataSource.data = results;
          this.isNameSearchActive = true;
        },
        error: (err) => {
          this.snackBar.open(`Search error (${err.status})`, 'Close', { duration: 5000 });
        }
      });
  }

  clearNameSearch() {
    this.nameSearchValue = '';
    this.isNameSearchActive = false;
    this.applyAllFilters();
  }

  probateSearchValue: string = '';
  isProbateSearchActive: boolean = false;

  applyProbateSearch() {
    const id = this.probateSearchValue.trim();
    if (!id) return;
    const params = new HttpParams().set('probateId', id).set('user', this.loggedInUserTag);
    this.http.get<ProbateSummary>('/charitycentral/getprobatesummarybyid', { params })
      .subscribe({
        next: (result) => {
          this.selection.clear();
          this.dataSource.data = [result];
          this.isProbateSearchActive = true;
        },
        error: () => {
          this.selection.clear();
          this.dataSource.data = [];
          this.isProbateSearchActive = true;
        }
      });
  }

  clearProbateSearch() {
    this.probateSearchValue = '';
    this.isProbateSearchActive = false;
    this.applyAllFilters();
  }

  applyDateSearch() {
    if (!this.startDate && !this.endDate) return;
    let params = new HttpParams().set('user', this.loggedInUserTag);
    if (this.startDate) params = params.set('dateFrom', this.startDate.format('YYYY-MM-DD'));
    if (this.endDate) params = params.set('dateTo', this.endDate.format('YYYY-MM-DD'));
    this.http.get<ProbateSummary[]>('/charitycentral/getprobatesummariesbydate', { params })
      .subscribe({
        next: (results) => {
          this.selection.clear();
          this.dataSource.data = results;
          this.isDateSearchActive = true;
        },
        error: (err) => {
          this.snackBar.open(`Search error (${err.status})`, 'Close', { duration: 5000 });
        }
      });
  }

  clearDateSearch() {
    this.startDate = null;
    this.endDate = null;
    this.isDateSearchActive = false;
    this.applyAllFilters();
  }

  applySearch() {
    if (this.probateSearchValue.trim()) {
      this.applyProbateSearch();
    } else if (this.nameSearchValue.trim()) {
      this.applyNameSearch();
    } else if (this.startDate || this.endDate) {
      this.applyDateSearch();
    }
  }

  applyQuickFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applyUserFilter() {
    this.applyAllFilters();
  }

  applyStatusFilter() {
    // Reload data from server with new status filters
    this.populateComponents();
  }

  applyAllFilters() {
    let filteredData = [...this.ProbateSummaries];

    // Apply Reporter filter first (Reporters only see their assigned probates)
    if (this.isReporterOnly()) {
      filteredData = filteredData.filter(
        p => p.assignedUser?.id === this.loggedInUser2?.id
      );
      // Reporters fetch all statuses from server — apply status filter client-side
      if (this.selectedStatusFilters.length > 0) {
        filteredData = filteredData.filter(p => this.selectedStatusFilters.includes(p.status));
      }
    }

    // Apply user filter (for Admins/Supervisors)
    if (this.selectedUserFilter) {
      if (this.selectedUserFilter === 'unassigned') {
        filteredData = filteredData.filter(p => !p.assignedUser);
      } else {
        filteredData = filteredData.filter(
          p => p.assignedUser?.id === (this.selectedUserFilter as User).id
        );
      }
    }

    // Status filter is applied server-side for Admins/Supervisors via getProbates()

    // Reloading the dataset means any previously selected rows now refer to stale
    // object instances that aren't in the visible table — clear the selection so
    // they can't sneak into the next assign call.
    this.selection.clear();
    this.dataSource.data = filteredData;
  }

  goToReport(element: ProbateSummary) {
    this.router.navigate(['/reporting', element.probateId], {
      state: { user: this.loggedInUser2 }
    });
  }

  // Assign selected rows
  assignSelectedProbates() {
    const assignedUser = this.assignToUserControl.value;
    if (!assignedUser || this.selection.isEmpty()) return;

    if (assignedUser === 'unassign') {

      //JP this is an unassign activity
      this.selection.selected.forEach(probate => {
        probate.assignedUser = null;
      });

      this.unassignProbates(this.selection.selected);

      this.snackBar.open(
        `Unassigned ${this.selection.selected.length} case(s)`,
        'Close',
        { duration: 3000 }
      );

    } else {

      this.selection.selected.forEach(probate => {
        probate.assignedUser = assignedUser;
      });

      this.saveProbates(this.selection.selected);

      this.snackBar.open(
        `Assigned ${this.selection.selected.length} case(s) to ${assignedUser.firstName} ${assignedUser.lastName}`,
        'Close',
        { duration: 3000 }
      );

    }

    this.selection.clear();
    this.dataSource._updateChangeSubscription();
  }

  // Checkbox helpers — operate on the rows currently rendered (filter → sort → paginate)
  // so "select all" always matches what the user can actually see. Individual selections
  // made on other pages are preserved.
  private getPageRows(): ProbateSummary[] {
    return this.renderedRows;
  }

  isAllSelected() {
    const pageRows = this.getPageRows();
    if (pageRows.length === 0) return false;
    return pageRows.every(row => this.selection.isSelected(row));
  }

  isSomeSelectedOnPage(): boolean {
    const pageRows = this.getPageRows();
    const anySelected = pageRows.some(row => this.selection.isSelected(row));
    return anySelected && !this.isAllSelected();
  }

  masterToggle() {
    const pageRows = this.getPageRows();
    if (this.isAllSelected()) {
      // Deselect just the visible rows. Off-page hand-picks (if any) are left alone so
      // an accidental double-click doesn't wipe more than the user can see.
      pageRows.forEach(row => this.selection.deselect(row));
    } else {
      // "Select all" means exactly these visible rows — replace any prior selection
      // (from an earlier page or a different sort) so the assign call uses only
      // what the user is currently looking at.
      this.selection.clear();
      pageRows.forEach(row => this.selection.select(row));
    }
  }

  extractRouteValues() {
    // Get user from the UserService (shared across components)
    this.loggedInUser2 = this.userService.loggedInUser;
    this.loggedInUserTag = `${this.loggedInUser2?.lastName}, ${this.loggedInUser2?.firstName}`;
  }

  populateComponents() {
    forkJoin([this.getProbates(), this.getUsers()]).subscribe(results => {
      this.ProbateSummaries = results[0];
      this.users = results[1].sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      // Pre-apply user filter for Supervisors, only on first load
      if (!this.hasInitialisedUserFilter) {
        this.hasInitialisedUserFilter = true;
        const isSupervisor = this.loggedInUser2?.roles?.some(r => r.id === 2 || r.code === 'Supervisor');
        if (isSupervisor) {
          const self = this.users.find(u => u.id === this.loggedInUser2?.id);
          if (self) this.selectedUserFilter = self;
        }

        if (this.canAssignProbates() && !this.displayedColumns.includes('reportedBy')) {
          const statusIndex = this.displayedColumns.indexOf('status');
          this.displayedColumns.splice(statusIndex, 0, 'reportedBy');
        }
        if (this.canAssignProbates() && !this.displayedColumns.includes('verifiedBy')) {
          const statusIndex = this.displayedColumns.indexOf('status');
          this.displayedColumns.splice(statusIndex, 0, 'verifiedBy');
        }
      }

      // Apply all filters (including Reporter-only filter)
      this.applyAllFilters();
    });
  }

  async saveProbates(probates: ProbateSummary[]) {
    const assignedUser = this.assignToUserControl.value as User;

    const parameters = new HttpParams()
      .set('useridtoassignto', assignedUser.id)
      .set('user', this.loggedInUserTag);

    const probateIds = probates.map(p => p.probateId);

    const response = this.http.post(
      "/charitycentral/saveprobateuserassignments",
      probateIds,
      { params: parameters }
    );

    return await lastValueFrom(response);
  }

  async unassignProbates(probates: ProbateSummary[]) {

    var stringProbates = JSON.stringify(probates);

    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag);

    var probateIds = probates.map(function (a) { return a.probateId });

    var response = this.http.post("/charitycentral/unassignprobates", probateIds, { params: parameters });

    return await lastValueFrom(response);

  }

  async getProbates() {
    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('includeNonCharitable', this.showNonCharitable.toString());

    // Reporters fetch all statuses so they can see assigned probates regardless of workflow status.
    // Admins/Supervisors use the selected status filter chips.
    const statusesToFetch = this.isReporterOnly()
      ? this.statusOptions.map(s => s.value)
      : this.selectedStatusFilters;

    for (const status of statusesToFetch) {
      parameters = parameters.append('statusesToGet', status.toString());
    }

    const response = this.http.get<ProbateSummary[]>("/charitycentral/getprobatesummaries", { params: parameters });
    return await lastValueFrom(response);
  }

  onShowNonCharitableChange() {
    this.populateComponents();
  }

  async getUsers() {
    const parameters = new HttpParams().set('user', this.loggedInUserTag);
    const result = this.http.get<User[]>('/charitycentral/getallusers', { params: parameters });
    return await lastValueFrom(result);
  }

  canAssignProbates(): boolean {
    if (!this.loggedInUser2 || !this.loggedInUser2.roles) {
      return false;
    }
    // Check if user has SystemAdministrator (id: 1) or Supervisor (id: 2) role
    return this.loggedInUser2.roles.some(role =>
      role.id === 1 || role.id === 2 || role.code === 'Supervisor'
    );
  }

  getStatusLabel(status: number): string {
    return this.statusOptions.find(s => s.value === status)?.label ?? 'Unknown';
  }

  getWeekOld(dateOfIssue: Date | string | null | undefined): number | string {
    if (!dateOfIssue) return '';
    const issueDate = new Date(dateOfIssue);
    if (isNaN(issueDate.getTime())) return '';
    return Math.floor((Date.now() - issueDate.getTime()) / 604800000);
  }

  compareUsers(u1: User | 'unassigned' | null, u2: User | 'unassigned' | null): boolean {
    if (u1 === u2) return true;
    if (typeof u1 === 'object' && u1 !== null && typeof u2 === 'object' && u2 !== null) {
      return u1.id === u2.id;
    }
    return false;
  }

  isReporterOnly(): boolean {
    if (!this.loggedInUser2?.roles) return false;
    // Reporter only if they have Reporter role but NOT SystemAdmin or Supervisor
    const hasReporter = this.loggedInUser2.roles.some(r => r.id === 4 || r.code === 'Reporter');
    const hasAdminOrSupervisor = this.canAssignProbates();
    return hasReporter && !hasAdminOrSupervisor;
  }
}
